import React, { useState, useEffect, useCallback, useMemo } from "react";
import "../RushMedia.css";
import {
  AppProps,
  Config,
  Tab,
  Video,
  PaginatedVideosResponse,
} from "../types";
import { MediaService } from "../services/api";
import { VideoPlayer } from "./VideoPlayer";
import { VideoItem } from "./VideoItem";
import { Skeleton } from "./Skeleton";

export const RushMedia: React.FC<AppProps> = ({
  pwsHost,
  showSlug,
  pageSlug,
  isAuthenticated,
  groupSlug,
  episodeSlug,
}) => {
  const [config, setConfig] = useState<Config | null>(null);
  const [activeTab, setActiveTab] = useState<string>(groupSlug || "");
  const [currentVideo, setCurrentVideo] = useState<Video | null>(null);
  const [videos, setVideos] = useState<Video[]>([]);
  const [resumeToken, setResumeToken] = useState<string | null>(null);
  const [hasMoreVideos, setHasMoreVideos] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [videosLoading, setVideosLoading] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [ActiveTabData, setActiveTabData] = useState<Tab | any>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const DITTOCAM_TAB = "Dittocam";
  // Use the simplified MediaService like the original plugin
  const mediaService = useMemo(
    () => new MediaService(pwsHost, showSlug),
    [pwsHost, showSlug],
  );

  // Load videos for a specific tab or search
  const loadVideos = useCallback(
    async (
      tabSlug: string,
      resume?: string,
      search?: string,
      isNewSearch: boolean = false,
    ) => {
      setVideosLoading(true);

      console.log("videosLoading", videosLoading);
      setError(null);

      try {
        let response: PaginatedVideosResponse;
        console.log("search", search);
        if (search) {
          response = await mediaService.getVideos(resume, { query: search });
          console.log("response -- search api", response);
        } else {
          response = await mediaService.getTabVideos(tabSlug, resume);
          console.log("first__---", tabSlug, resume);
        }

        if (isNewSearch || !resume) {
          setVideos(response.videos || []);
          console.log(
            "response, isNewSearch, resume",
            response,
            isNewSearch,
            resume,
          );
          if (
            !episodeSlug &&
            !currentVideo &&
            response.videos &&
            response.videos.length > 0
          ) {
            console.log(
              `🎬 Auto-loading first video from search/tab: ${response.videos[0].title}`,
            );
            setCurrentVideo(response.videos[0]);
          } else {
            console.log("setActiveTabData!!!!", response);
            setActiveTabData(response);
          }
        } else {
          console.log(
            "setVideos(prev => [...prev, ...(response.videos || [])]);",
            response,
          );
          setVideos((prev) => [...prev, ...(response.videos || [])]);
          setActiveTabData(response);
        }
        console.log("response -- setResumeToken", response);
        setResumeToken(response?.resume || null);
        setHasMoreVideos(response.pagination?.hasMore || false);
      } catch (error) {
        console.error("Failed to load videos:", error);
        setError("Failed to load videos. Please check your connection.");
        if (isNewSearch || !resume) {
          setVideos([]);
        }
      } finally {
        setVideosLoading(false);
      }
    },
    [mediaService, episodeSlug],
  );

  console.log("videos", videos);

  useEffect(() => {
    const loadConfig = async () => {
      setError(null);
      try {
        console.log(
          `🌐 Loading config from: https://${pwsHost}/media/${showSlug}/config?includeVideos=true&perPage=${itemsPerPage}`,
        );
        const configData = await mediaService.getConfig(true, itemsPerPage);
        console.log("configData__--__--", configData);
        setConfig(configData);

        // Set initial active tab if not provided
        if (!activeTab && configData.tabs.length > 0) {
          setActiveTab(configData.tabs[0].slug);
        }

        // If config includes videos for the active tab, use them
        const currentTabData = configData.tabs.find(
          (tab) => tab.slug === (activeTab || configData.tabs[0].slug),
        );
        setActiveTabData(currentTabData || null);
        if (currentTabData?.videos) {
          console.log(
            `📹 Using videos from config for tab: ${currentTabData.slug}`,
          );
          setVideos(currentTabData.videos);
          console.log("currentTabData", currentTabData);
          setResumeToken(currentTabData?.resume || null);
          setHasMoreVideos(currentTabData.videos.length >= itemsPerPage);
          if (!episodeSlug && currentTabData.videos.length > 0) {
            console.log(
              `🎬 Auto-loading first video: ${currentTabData.videos[0].title}`,
            );
            setCurrentVideo(currentTabData.videos[0]);
          }
        }
      } catch (error) {
        console.error("Failed to load config:", error);
        setError("Failed to load configuration. Please check your connection.");
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [mediaService, pwsHost, showSlug, activeTab]);

  console.log("resumeToken", resumeToken);

  useEffect(() => {
    const loadInitialVideo = async () => {
      if (!episodeSlug) return;

      setVideoLoading(true);
      setError(null);
      try {
        console.log(
          `🎬 Loading video: https://${pwsHost}/media/${showSlug}/videos/slug/${episodeSlug}`,
        );
        const response = await mediaService.getMediaBySlug(episodeSlug);
        setCurrentVideo(response.video);
      } catch (error) {
        console.error("Failed to load initial video:", error);
        setError("Failed to load video. Please check your connection.");
      } finally {
        setVideoLoading(false);
      }
    };

    loadInitialVideo();
  }, [episodeSlug, mediaService, pwsHost, showSlug]);

  console.log("resumeToken", resumeToken);

  useEffect(() => {
    if (activeTab && config) {
      const currentTabData = config.tabs.find((tab) => tab.slug === activeTab);
      if (!currentTabData?.videos) {
        console.log(`📋 Loading videos for tab: ${activeTab}`);
        loadVideos(activeTab, undefined, searchTerm, true);
      } else {
        console.log(`📋 Using config videos for tab: ${activeTab}`);
        setVideos(currentTabData.videos);
        // setResumeToken(null);
        setHasMoreVideos(currentTabData.videos.length >= 20);
      }
    }
  }, [activeTab, config, loadVideos]);

  useEffect(() => {
    setCurrentPage(1);
  }, []);

  const totalPages = ActiveTabData?.pagination?.pages;
  const currentPages = ActiveTabData?.pagination?.current;

  const paginatedVideos = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return videos.slice(start, start + itemsPerPage);
  }, [videos, currentPage]);

  const handleTabChange = (tabSlug: string) => {
    setActiveTab(tabSlug);
    setSearchTerm("");
    setError(null);
    setCurrentPage(1);
    setSearchValue("");
  };

  const handleVideoClick = (video: Video) => {
    setCurrentVideo(video);

    const url = `/${pageSlug}/${activeTab}/${video.slug}`;
    window.history.pushState({}, "", url);
    console.log(`🎬 Selected video: ${video.title}`);
  };

  const handleLoadMore = () => {
    if (resumeToken && !videosLoading) {
      loadVideos(activeTab, resumeToken, searchTerm, false);
      setCurrentPage((prev) => prev + 1); // Move to next page after loading more
    }
  };

  const handleNextPage = () => {
    const nextPage = currentPage + 1;
    const totalLoadedVideos = videos.length;
    const requiredVideos = nextPage * itemsPerPage;

    if (totalLoadedVideos >= requiredVideos) {
      setCurrentPage(nextPage);
    } else if (resumeToken && !videosLoading) {
      // Load more videos, then increment page after loading
      loadVideos(activeTab, resumeToken, searchTerm, false);
      setCurrentPage(nextPage);
    }
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    if (searchTerm) {
      setSearchValue(searchTerm);
      if (searchTerm.trim()) {
        loadVideos(activeTab, undefined, searchTerm, true);
      } else {
        const currentTabData = config?.tabs.find(
          (tab) => tab.slug === activeTab,
        );
        if (currentTabData?.videos) {
          setVideos(currentTabData.videos);
          setResumeToken(null);
          setHasMoreVideos(currentTabData.videos.length >= itemsPerPage);
        } else {
          loadVideos(activeTab, undefined, undefined, true);
        }
      }
    }
  };

  console.log("videoLoadingvideoLoadingvideoLoading", videosLoading);

  const currentTab = config?.tabs.find((tab) => tab.slug === activeTab);

  console.log("paginatedVideos.length", paginatedVideos.length);

  if (error && !loading && !videosLoading) {
    return (
      <div className="component-zype-media">
        <div
          style={{
            padding: "20px",
            backgroundColor: "#333",
            color: "#ff6b6b",
            textAlign: "center",
            borderRadius: "4px",
            margin: "20px",
          }}
        >
          <h3>⚠️ Error</h3>
          <p>{error}</p>
          <p style={{ fontSize: "14px", color: "#aaa" }}>
            API Endpoint: https://{pwsHost}/media/{showSlug}/
          </p>
          <button
            onClick={() => window.location.reload()}
            className="error-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show loading state
  // if (loading) {
  //     return (
  //         <div className="component-zype-media">
  //             <div style={{
  //                 padding: '10px',
  //                 backgroundColor: '#333',
  //                 marginBottom: '10px',
  //                 borderRadius: '4px',
  //                 fontSize: '14px'
  //             }}>
  //                 🌐 Loading from: https://{pwsHost}/media/{showSlug}/config?includeVideos=true&perPage=itemsPerPage
  //             </div>
  //             <div className="component-zype-player">
  //                 <Skeleton shape="16x9" />
  //             </div>
  //             <div className="component-zype-tabs">
  //                 <div className="component-zype-nav-bar">
  //                     <div className="tab-headers-container">
  //                         {[1, 2, 3].map(i => (
  //                             <Skeleton key={i} />
  //                         ))}
  //                     </div>
  //                 </div>
  //             </div>
  //         </div>
  //     );
  // }

  return (
    <div className="component-zype-media">
      <div
        style={{ display: "flex", justifyContent: "center", margin: "8px 0" }}
      >
        <a
          href="https://www.rushlimbaugh.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="et_pb_image_wrap">
            <img
              src="https://www.rushlimbaugh.com/wp-content/uploads/2015/08/ShowLogo.png"
              alt="The Pioneer of Conservative Talk Radio"
              title="The Pioneer of Conservative Talk Radio"
              sizes="(min-width: 0px) and (max-width: 480px) 480px, (min-width: 481px) 721px, 100vw"
              style={{
                maxWidth: "100%",
                height: "auto",
                display: "block",
                borderRadius: "8px",
                boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
              }}
            />
          </span>
        </a>
      </div>

      {/* Mega Dittos Header */}
      <div className="mega-dittos-header">
        <div className="mega-dittos-title">
          MEGA DITTOS
          <span className="mega-dittos-registered">®</span>
        </div>

        <div className="mega-dittos-subtitle">
          The Rush Limbaugh Audio &amp; Video Archive
        </div>
      </div>

      <div className="player-content-wrapper">
        <VideoPlayer
          video={currentVideo || undefined}
          isLoading={loading}
          pwsHost={pwsHost}
          showSlug={showSlug}
        />

        <div className="player-sidebar">
          {currentVideo && (
            <div
              style={{
                padding: "10px",
                backgroundColor: "#2a2a2a",
                borderRadius: "8px",
              }}
            >
              <h3 style={{ margin: "0 0 1px 0", color: "#fff" }}>
                Now Playing
              </h3>
              <h4 style={{ margin: "0 0 1px 0", color: "#4a9eff" }}>
                {currentVideo.title}
              </h4>
              {currentVideo.description && (
                <p
                  style={{
                    margin: "0",
                    color: "#ccc",
                    fontSize: "14px",
                    lineHeight: "1.5",
                  }}
                >
                  {currentVideo.description.length > 200
                    ? `${currentVideo.description.substring(0, 200)}...`
                    : currentVideo.description}
                </p>
              )}
              {currentVideo.created_at && (
                <p
                  style={{
                    margin: "6px 0 0 0",
                    color: "#999",
                    fontSize: "10px",
                  }}
                >
                  Published:{" "}
                  {new Date(currentVideo.created_at).toLocaleDateString()}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* <div className="ads">
                Ad space placeholder
            </div> */}

      <div className="component-zype-tabs">
        <div className="component-zype-nav-bar">
          <div className="tab-headers-container">
            {loading || !config
              ? Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="zype-tab-header skeleton shape-text"
                    style={{
                      width: 90,
                      marginTop: 130,
                      height: 24,
                      marginRight: 8,
                    }}
                  />
                ))
              : config.tabs.map((tab: Tab) => (
                  <button
                    key={tab.slug}
                    className={`zype-tab-header ${activeTab === tab?.slug ? "active" : ""}`}
                    onClick={() => handleTabChange(tab?.slug)}
                  >
                    {tab.title === DITTOCAM_TAB ? (
                      <span>
                        {DITTOCAM_TAB}
                        <sup style={{ fontFamily: "Arial" }}>®</sup>
                      </span>
                    ) : (
                      tab?.title
                    )}
                  </button>
                ))}
          </div>
          {loading || !config ? (
            <div
              className="search-bar-container skeleton"
              style={{
                width: 180,
                height: 36,
                marginTop: 130,
                borderRadius: 8,
                marginLeft: 12,
              }}
            />
          ) : (
            <form className="search-bar-container" onSubmit={handleSearch}>
              <input
                className="search-bar-input"
                type="text"
                placeholder="Search videos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button className="search-bar-button" type="submit">
                SEARCH
              </button>
            </form>
          )}
        </div>

        <div className="tabs-container">
          <div className={`component-zype-tab ${activeTab ? "active" : ""}`}>
            <div
              className={`tab-header ${currentTab?.title ? "with-title" : ""}`}
            >
              {searchValue ? (
                <h2 className="tab-title">{`Search results for "${searchValue}":`}</h2>
              ) : (
                <h2 className="tab-title"></h2>
              )}
              {loading || !config ? (
                // <div className="search-bar-container skeleton" style={{ width: 180, height: 36, borderRadius: 8, margin:20 }} />
                <></>
              ) : (
                <div className="pagination-controls">
                  <button
                    className="page-arrow"
                    onClick={() =>
                      setCurrentPage((current) => Math.max(1, current - 1))
                    }
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    {"<"}
                  </button>
                  <span>
                    Page {currentPage} of {totalPages || 1}
                  </span>
                  <button
                    className="page-arrow"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages || totalPages === 0}
                    aria-label="Next page"
                  >
                    {">"}
                  </button>
                </div>
              )}
            </div>

            {error ? (
              <div
                style={{
                  padding: "20px",
                  backgroundColor: "#333",
                  color: "#ff6b6b",
                  textAlign: "center",
                  borderRadius: "4px",
                }}
              >
                {error}
              </div>
            ) : (
              <div className="videos-container">
                {videosLoading || loading
                  ? Array.from({ length: 12 }).map((_, index) => (
                      <VideoItem
                        key={index}
                        isLoading={loading}
                        pageSlug={pageSlug}
                        groupSlug={activeTab}
                      />
                    ))
                  : paginatedVideos?.map((video: Video) => (
                      <VideoItem
                        key={video.id}
                        video={video}
                        onVideoClick={handleVideoClick}
                        pageSlug={pageSlug}
                        groupSlug={activeTab}
                        isLoading={loading}
                      />
                    ))}

                {/* {videosLoading && paginatedVideos.length > 0 && (
                                    Array.from({ length: 4 }).map((_, index) => (
                                        <VideoItem
                                            key={`loading-${index}`}
                                            isLoading={loading}
                                            pageSlug={pageSlug}
                                            groupSlug={activeTab}
                                        />
                                    ))
                                )} */}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
